import { CSSProperties, Dispatch, useEffect, useRef, useState } from 'react';
import { TSelectObject } from 'types/dropdown';
import styles from 'styles/components/common/elements/MultipleSelectDropdown.module.scss';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron_down.svg';
import { ReactComponent as ChevronUpIcon } from 'assets/icons/chevron_up.svg';
import { ReactComponent as ResetIcon } from 'assets/icons/reset.svg';
import { ReactComponent as CheckOffIcon } from 'assets/icons/check_off.svg';
import { ReactComponent as CheckOnIcon } from 'assets/icons/check_on.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import classNames from 'classnames';
import { ChartInfo } from 'constants/chart/common/chartInfo';

type TProps = {
  lists: TSelectObject[];
  selectedChartList: (string | number)[];
  setSelectedChartList: Dispatch<React.SetStateAction<(string | number)[]>>;
  readOnly?: boolean;
  disabledStyle?: CSSProperties;
};

const MultipleSelectDropdown = ({
  lists,
  selectedChartList,
  setSelectedChartList,
  readOnly,
  disabledStyle,
}: TProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [showSelectedOptionList, setShowSelectedOptionList] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const text =
    selectedChartList
      .map((item) => ChartInfo.find((chart) => chart.type === item)?.name)
      .join(', ') || '';

  const onClickChart = (option: string | number) => {
    const selectedType = ChartInfo.find((item) => item.name === option)?.type;

    if (selectedChartList.includes(selectedType as number)) {
      setSelectedChartList(
        selectedChartList.filter((item) => item !== selectedType)
      );
    } else {
      setSelectedChartList([...selectedChartList, selectedType as number]);
    }
  };

  const onClickSelectAll = () => {
    if (selectAll) {
      setSelectedChartList([]);
    } else {
      setSelectedChartList(lists.map((list) => list.value));
    }
    setSelectAll(!selectAll);
  };

  const onClickReset = () => {
    setSelectedChartList([]);
    setSelectAll(false);
  };

  useEffect(() => {
    selectedChartList.length === lists.length
      ? setSelectAll(true)
      : setSelectAll(false);
  }, [selectAll, selectedChartList, lists]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSelectedOptionList(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div ref={dropdownRef}>
      <button
        type="button"
        className={styles.selected_options}
        onClick={() => setShowSelectedOptionList(!showSelectedOptionList)}
        style={readOnly ? disabledStyle : undefined}
        disabled={readOnly}
      >
        {selectedChartList.length > 0 ? (
          <span className={styles.selected_list}>{text}</span>
        ) : (
          <span style={{ color: STYLES_VARIABLES.GRAY_06 }}>
            차트 종류를 선택해 주세요.
          </span>
        )}
        {showSelectedOptionList ? (
          <ChevronUpIcon
            width={24}
            height={24}
            fill={STYLES_VARIABLES.GRAY_07}
          />
        ) : (
          <ChevronDownIcon
            width={24}
            height={24}
            fill={STYLES_VARIABLES.GRAY_07}
          />
        )}
      </button>
      {showSelectedOptionList && (
        <div className={styles.dropdown_list}>
          <ul>
            <li
              className={classNames(styles.select_all, {
                [styles.active_select]: selectAll,
              })}
            >
              <div
                className="flex items_center"
                onClick={
                  readOnly
                    ? () => {
                        return;
                      }
                    : onClickSelectAll
                }
              >
                {selectAll ? (
                  <CheckOnIcon width={20} height={20} />
                ) : (
                  <CheckOffIcon width={20} height={20} />
                )}
                <span className="ml8">전체 선택</span>
              </div>
              <div className="flex items_center">
                <ResetIcon
                  width={18}
                  height={18}
                  fill={STYLES_VARIABLES.GRAY_07}
                  onClick={
                    readOnly
                      ? () => {
                          return;
                        }
                      : onClickReset
                  }
                />
                <span className="fw700 ml8">
                  총{' '}
                  <span className={styles.color_text}>
                    {selectedChartList.length}
                  </span>
                  개
                </span>
              </div>
            </li>
            {lists.map((list, index) => (
              <li
                key={index}
                onClick={
                  readOnly
                    ? () => {
                        return;
                      }
                    : () => onClickChart(list.label)
                }
                className={classNames(styles.chart, {
                  [styles.selected]: selectedChartList.includes(list.value),
                })}
              >
                {list.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultipleSelectDropdown;
